import { Box, TextField, useMediaQuery } from '@mui/material';
import { FormikContextType, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import AddressForm from '../../components/forms/AddressForm';
import { AddressBookPicker, FixedAddress, RequiredLabel, Title, ToggleSwitch } from '../../components/_mui';
import { PICKED_ADDRESS_ID, RECEIVER_TAX_NUMBER } from '../../constants/shipping';
import { ADDRESS_CHOICE_TABS, ADDRESS_HALF_WIDTH_BREAKPOINT } from '../../constants/ui';
import useWindowSize from '../../hooks/useWindowSize';
import { selectInitialAddress, selectTaxRequiredCountries } from '../../store/selectors/init';
import { selectAllowedCountries } from '../../store/selectors/marketplace';
import { IFixedAddress } from '../../types/api';
import { ITaxRequiredCountries } from '../../types/shell';
import {
  IAddressSuggestion,
  ICustomOrFixedAddressForm,
  IFixedAddressForm,
  IUserAddressForm,
} from '../../types/shipping';
import { AddressChoiceEnum } from '../../types/ui';
import { formatApiAddress, isAddressBookAvailable } from '../../util/address';
import { getLockedCountry, getTaxRequirementInfo, isCountryAllowed } from '../../util/helpers';

interface IProps {
  addressBook?: IFixedAddress[];
  fixedAddress?: IFixedAddress[];
}

const AddressFormContainer: React.FC<IProps> = ({ addressBook, fixedAddress }) => {
  const { mobile } = useWindowSize();
  const isHalfWidthCollapsed = useMediaQuery(`(max-width:${ADDRESS_HALF_WIDTH_BREAKPOINT}px)`);

  const form = useFormikContext<ICustomOrFixedAddressForm | IUserAddressForm | IFixedAddressForm>();
  const taxRequiredCountries = useSelector(selectTaxRequiredCountries);
  const allowedCountries = useSelector(selectAllowedCountries);
  const initialAddress = useSelector(selectInitialAddress);
  const initialTaxNumber = initialAddress?.receiver_tax_number ?? '';
  const [deliveryAddressTab, setDeliveryAddressTab] = useState<AddressChoiceEnum>(AddressChoiceEnum.Select);
  const prevCountryRef = useRef<string | undefined>();
  const {
    setFieldValue,
    setFieldTouched,
    setFieldError,
    submitCount,
    values,
    setValues,
    resetForm,
    setErrors,
    setTouched,
    touched,
    errors,
  } = form;

  const { receiver_tax_number: receiverTaxNumber, userAddress, picked_address_id: pickedAddressId } = values;
  const { receiver_tax_number: receiverTaxNumberError } = errors;

  const isFixedAddress = useMemo(() => !!fixedAddress?.length, [fixedAddress]);
  const isCountryLocked = !!getLockedCountry(allowedCountries);
  const isBookAvailable = useMemo(
    () => isAddressBookAvailable(addressBook, allowedCountries),
    [addressBook, allowedCountries],
  );
  const isAddressDisabled = useCallback(
    (address: IFixedAddress) => !isCountryAllowed(address.country, allowedCountries),
    [allowedCountries],
  );
  const userCountry = useMemo(() => {
    return (form.values as ICustomOrFixedAddressForm).userAddress?.receiver_country;
  }, [form.values]);

  const getTaxInfoForCountry = useCallback(
    (countryCode: string | undefined) => {
      if (!countryCode || !taxRequiredCountries) {
        return { isRequired: false, label: 'Tax Number' };
      }

      const taxInfo = taxRequiredCountries[countryCode as keyof ITaxRequiredCountries];

      return {
        isRequired: !!taxInfo,
        label: taxInfo?.label || 'Tax Number',
      };
    },
    [taxRequiredCountries],
  );

  const clearTaxNumber = useCallback(() => {
    setFieldValue(RECEIVER_TAX_NUMBER, '');
    setFieldTouched(RECEIVER_TAX_NUMBER, false);
    setFieldError(RECEIVER_TAX_NUMBER, undefined);
  }, [setFieldError, setFieldValue, setFieldTouched]);

  const createTaxInput = useCallback(
    (isRequired: boolean, label: string) => (
      <TextField
        required={isRequired}
        label={label}
        name={RECEIVER_TAX_NUMBER}
        onBlur={() => setFieldTouched(RECEIVER_TAX_NUMBER)}
        error={submitCount > 0 ? !!receiverTaxNumberError : false}
        helperText={touched?.[RECEIVER_TAX_NUMBER] ? errors?.[RECEIVER_TAX_NUMBER] : undefined}
        value={receiverTaxNumber ?? ''}
        onChange={(e) => setFieldValue(RECEIVER_TAX_NUMBER, e.target.value)}
        fullWidth
        size="medium"
      />
    ),
    [receiverTaxNumber, receiverTaxNumberError, setFieldTouched, submitCount, touched, errors, setFieldValue],
  );

  const handleAddressSelect = useCallback(
    (address: IAddressSuggestion) => {
      const addressData = formatApiAddress(address);
      if (addressData) {
        setValues({
          ...values,
          userAddress: { ...userAddress, ...addressData },
        });
      }
    },
    [values, userAddress, setValues],
  );

  const handleAddressChange = useCallback(
    (id: string, addresses: IFixedAddress[]) => {
      setFieldValue(PICKED_ADDRESS_ID, id, true);
      setFieldError(PICKED_ADDRESS_ID, undefined);

      const { isRequired: newAddressTaxRequired } = getTaxRequirementInfo(addresses, id, taxRequiredCountries);
      if (!newAddressTaxRequired && receiverTaxNumber) {
        clearTaxNumber();
      }
    },
    [receiverTaxNumber, setFieldValue, setFieldError, taxRequiredCountries, clearTaxNumber],
  );

  const renderAddressFields = useCallback(
    (addresses?: IFixedAddress[]) => {
      if (!addresses) {
        return null;
      }

      const hasMultipleChoices = addresses.length > 1;
      const currentMode = values.mode;
      const isSingleAddressInTabMode = currentMode === AddressChoiceEnum.Select && !hasMultipleChoices;

      let taxInfo: { isRequired: boolean; label: string };

      if (isSingleAddressInTabMode) {
        const singleAddress = addresses[0];
        taxInfo = getTaxInfoForCountry(singleAddress?.country);
      } else {
        const { isRequired, taxLabel: title } = getTaxRequirementInfo(addresses, pickedAddressId, taxRequiredCountries);
        taxInfo = { isRequired, label: title || 'Tax Number' };
      }

      const fixedAddressTaxInfo = getTaxInfoForCountry(addresses[0]?.country);

      if (hasMultipleChoices) {
        // Copy before sorting — `addresses` is derived from the Redux `initialAddress`
        // selector; `Array.prototype.sort` mutates in place and would corrupt the
        // store-derived (redux-persist'd) ordering.
        const sortedAddresses = [...addresses].sort((a, b) =>
          (a.label ?? a.address1).toLowerCase().localeCompare((b.label ?? b.address1).toLowerCase()),
        );

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <AddressBookPicker
              value={pickedAddressId}
              onBlur={() => setFieldTouched(PICKED_ADDRESS_ID, true, false)}
              onChange={(id) => handleAddressChange(id, addresses)}
              addresses={sortedAddresses}
              isOptionDisabled={isAddressDisabled}
              error={touched?.picked_address_id ? errors?.picked_address_id : undefined}
            />
            {taxInfo.isRequired && createTaxInput(true, taxInfo.label)}
          </Box>
        );
      }

      // Single address case
      const shouldShowTaxInput = fixedAddressTaxInfo.isRequired || (isSingleAddressInTabMode && taxInfo.isRequired);
      const taxLabel = isSingleAddressInTabMode ? taxInfo.label : fixedAddressTaxInfo.label;

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FixedAddress address={addresses[0]} />
          {shouldShowTaxInput && createTaxInput(shouldShowTaxInput, taxLabel)}
        </Box>
      );
    },
    [
      form,
      isFixedAddress,
      taxRequiredCountries,
      isAddressDisabled,
      getTaxInfoForCountry,
      createTaxInput,
      handleAddressChange,
    ],
  );

  const shippingFormLayout = useMemo(() => {
    const taxInfo = getTaxInfoForCountry(userAddress?.receiver_country);

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <AddressForm
            form={form as FormikContextType<ICustomOrFixedAddressForm>}
            onReset={resetForm}
            onSelect={handleAddressSelect}
            allowedCountries={allowedCountries}
            isCountryLocked={isCountryLocked}
          />
          {taxInfo.isRequired && (
            <TextField
              required
              label={taxInfo.label}
              name={RECEIVER_TAX_NUMBER}
              onBlur={() => setFieldTouched(RECEIVER_TAX_NUMBER)}
              error={submitCount > 0 ? !!receiverTaxNumberError : false}
              helperText={touched?.[RECEIVER_TAX_NUMBER] ? errors?.[RECEIVER_TAX_NUMBER] : undefined}
              value={receiverTaxNumber ?? ''}
              onChange={(e) => setFieldValue(RECEIVER_TAX_NUMBER, e.target.value)}
              fullWidth
              size="medium"
            />
          )}
        </Box>
        <RequiredLabel />
      </Box>
    );
  }, [
    form,
    handleAddressSelect,
    getTaxInfoForCountry,
    touched,
    errors,
    receiverTaxNumber,
    receiverTaxNumberError,
    setFieldTouched,
    submitCount,
    allowedCountries,
    isCountryLocked,
  ]);

  const multiChoiceLayout = useMemo(() => {
    const changeTab = (mode: AddressChoiceEnum) => {
      setErrors({});
      setTouched({});
      setFieldValue(RECEIVER_TAX_NUMBER, mode === AddressChoiceEnum.Form ? initialTaxNumber : '');
      setFieldTouched(RECEIVER_TAX_NUMBER, false);
      setFieldError(RECEIVER_TAX_NUMBER, undefined);
      setFieldValue('mode', mode);
      setDeliveryAddressTab(mode);

      setTimeout(() => form.validateForm(), 0);
    };

    const halfWidthSx = isHalfWidthCollapsed || mobile ? { width: '100%' } : { width: 'calc(50% - 8px)' };

    return (
      <>
        <ToggleSwitch
          tabs={ADDRESS_CHOICE_TABS}
          value={deliveryAddressTab}
          onChange={changeTab}
          sx={{ mb: 3, ...halfWidthSx }}
        />
        {deliveryAddressTab === AddressChoiceEnum.Select && (
          <Box sx={halfWidthSx}>{renderAddressFields(addressBook)}</Box>
        )}
        {deliveryAddressTab === AddressChoiceEnum.Form && shippingFormLayout}
      </>
    );
  }, [
    form,
    shippingFormLayout,
    renderAddressFields,
    addressBook,
    deliveryAddressTab,
    mobile,
    isHalfWidthCollapsed,
    setErrors,
    setTouched,
    setFieldValue,
  ]);

  // handle country changes and clear tax number when needed
  useEffect(() => {
    const currentMode = values.mode;

    // handle both Form mode and no mode (plain address form)
    const isFormMode = currentMode === AddressChoiceEnum.Form || currentMode === undefined;

    if (isFormMode && userCountry !== prevCountryRef.current) {
      // check if tax number should be cleared when country changes
      const { isRequired: newCountryRequiresTax } = getTaxInfoForCountry(userCountry);

      if (!newCountryRequiresTax && receiverTaxNumber) {
        clearTaxNumber();
      }

      prevCountryRef.current = userCountry;
    }
  }, [userCountry, getTaxInfoForCountry, clearTaxNumber, values.mode, receiverTaxNumber]);

  const renderContent = useCallback(() => {
    if (isFixedAddress) {
      return renderAddressFields(fixedAddress);
    }

    return isBookAvailable ? multiChoiceLayout : shippingFormLayout;
  }, [isBookAvailable, fixedAddress, multiChoiceLayout, renderAddressFields, isFixedAddress, shippingFormLayout]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Title>Delivery address</Title>
      {renderContent()}
    </Box>
  );
};

export default AddressFormContainer;
